// Store de facturation partagé (frontend uniquement).
//
// Regroupe les abonnements à la plateforme et les paiements associés. Aucune
// donnée fictive : les collections démarrent vides et ne contiennent que ce qui
// est réellement créé depuis l'espace Super Admin. Les prix proviennent toujours
// de la source de vérité `@/config/formules`.
//
// Relations garanties :
//   compte (propriétaire / agence / utilisateur) → abonnement → paiement
// Un abonnement est rattaché à un compte ; un paiement est rattaché à un
// abonnement (donc à son compte et à sa formule). Le jour où la base est
// branchée, ce store devient les tables `abonnements` / `paiements`.

import type { Periodicite } from "@/config/formules";
import { METHODES_PAIEMENT, type PaiementMethode } from "@/config/paiement";

export type { PaiementMethode } from "@/config/paiement";

export type AbonnementStatut = "actif" | "en_attente" | "expire" | "suspendu" | "annule";

export interface PlatformAbonnement {
  id: string;
  /** Identifiant du compte (table `comptes` du Super Admin) ; vide si non rattaché. */
  compteId: string;
  compteNom: string;
  compteType: string;
  formuleId: string;
  periodicite: Periodicite;
  montant: number;
  statut: AbonnementStatut;
  dateDebut: string;
  dateFin: string;
  renouvellement: boolean;
  createdAt: string;
}

export type PlatformAbonnementInput = Omit<PlatformAbonnement, "id" | "createdAt">;

export const ABONNEMENT_STATUT_LABELS: Record<AbonnementStatut, string> = {
  actif: "Actif",
  en_attente: "En attente",
  expire: "Expiré",
  suspendu: "Suspendu",
  annule: "Annulé",
};

export const ABONNEMENT_STATUT_TONES: Record<
  AbonnementStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger" | "success"
> = {
  actif: "secondary",
  en_attente: "accent",
  expire: "primary",
  suspendu: "danger",
  annule: "neutral",
};

export const ABONNEMENT_STATUT_OPTIONS = (Object.keys(ABONNEMENT_STATUT_LABELS) as AbonnementStatut[]).map((value) => ({
  value,
  label: ABONNEMENT_STATUT_LABELS[value],
}));

export function abonnementReference(id: string): string {
  return `ABO-${id.slice(-6).toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/* Paiements                                                           */
/* ------------------------------------------------------------------ */

// Moyens de paiement STRICTEMENT limités aux trois moyens autorisés
// (voir `@/config/paiement`) : Orange Money, Moov Money, Espèces.
// Statistiques alignées sur les contraintes CHECK de la base
// (`paiements_abonnements_statut_check`).
export type PaiementStatut = "en_attente" | "valide" | "rejete" | "annule";

/** État du suivi de l'e-mail de validation envoyé après validation du paiement. */
export type EmailValidationStatut = "non_envoye" | "en_cours" | "envoye" | "echec";

export interface PlatformPaiement {
  id: string;
  abonnementId: string;
  compteId: string;
  compteNom: string;
  formuleId: string;
  montant: number;
  date: string;
  methode: PaiementMethode;
  reference: string;
  statut: PaiementStatut;
  /** Période réglée (libellé lisible : mensuel / annuel, ou période concernée). */
  periode: string;
  /** Chemin Storage de la preuve (bucket privé) — vide si aucune preuve. */
  preuveUrl: string;
  /** Commentaire / motif (ex. motif de refus saisi par le Super Admin). */
  commentaire: string;
  createdAt: string;
  /** État réel de l'e-mail de validation (suivi serveur, jamais exposé en erreur brute). */
  emailStatut: EmailValidationStatut;
  /** Message technique de l'échec d'envoi (affiché uniquement au Super Admin). */
  emailErreur: string;
}

export type PlatformPaiementInput = Omit<
  PlatformPaiement,
  "id" | "createdAt" | "preuveUrl" | "commentaire" | "emailStatut" | "emailErreur"
> & {
  preuveUrl?: string;
  commentaire?: string;
};

export const PAIEMENT_METHODE_LABELS = METHODES_PAIEMENT.reduce<Record<PaiementMethode, string>>(
  (acc, methode) => {
    acc[methode.value] = methode.label;
    return acc;
  },
  {} as Record<PaiementMethode, string>
);

export const PAIEMENT_METHODE_ICONS = METHODES_PAIEMENT.reduce<Record<PaiementMethode, string>>(
  (acc, methode) => {
    acc[methode.value] = methode.icon;
    return acc;
  },
  {} as Record<PaiementMethode, string>
);

export const PAIEMENT_METHODE_OPTIONS = METHODES_PAIEMENT.map((methode) => ({
  value: methode.value,
  label: methode.label,
}));

export const PAIEMENT_STATUT_LABELS: Record<PaiementStatut, string> = {
  en_attente: "En attente",
  valide: "Validé",
  rejete: "Rejeté",
  annule: "Annulé",
};

export const PAIEMENT_STATUT_TONES: Record<
  PaiementStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger" | "success"
> = {
  en_attente: "accent",
  valide: "secondary",
  rejete: "danger",
  annule: "neutral",
};

export const PAIEMENT_STATUT_OPTIONS = (Object.keys(PAIEMENT_STATUT_LABELS) as PaiementStatut[]).map((value) => ({
  value,
  label: PAIEMENT_STATUT_LABELS[value],
}));

export function paiementReference(id: string): string {
  return `PAY-${id.slice(-6).toUpperCase()}`;
}

/* ------------------------------------------------------------------ */
/* Suivi de l'e-mail de validation                                     */
/* ------------------------------------------------------------------ */

export const EMAIL_VALIDATION_LABELS: Record<EmailValidationStatut, string> = {
  non_envoye: "Non envoyé",
  en_cours: "En cours",
  envoye: "Envoyé",
  echec: "Échec",
};

export const EMAIL_VALIDATION_TONES: Record<
  EmailValidationStatut,
  "primary" | "accent" | "secondary" | "neutral" | "danger" | "success"
> = {
  non_envoye: "neutral",
  en_cours: "accent",
  envoye: "secondary",
  echec: "danger",
};

export const EMAIL_VALIDATION_ICONS: Record<EmailValidationStatut, string> = {
  non_envoye: "ri-mail-line",
  en_cours: "ri-loader-4-line",
  envoye: "ri-mail-check-line",
  echec: "ri-mail-close-line",
};

/** Le renvoi n'est proposé que pour un paiement validé non encore envoyé. */
export function peutRenvoyerEmailValidation(paiement: PlatformPaiement): boolean {
  return paiement.statut === "valide" && paiement.emailStatut !== "envoye" && paiement.emailStatut !== "en_cours";
}

/** Modèle de données prévu pour Supabase (tables `abonnements` / `paiements`). */